import React, { CSSProperties, useRef, useState } from 'react';
import { IframeProps } from './types';

import './VideoIframe.css';
import { FontAwesomeIcon } from '../../FontAwesomeIcon';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import { LoadingOutlined } from '@ant-design/icons';
import { getImageDimensions } from '../../../lib/landingPage';
import { isIOS } from 'react-device-detect';

enum FacadeState {
    Initial,
    Loading,
    Ready,
}

// eslint-disable-next-line complexity, max-lines-per-function
export const VimeoIframe = (p: IframeProps): JSX.Element => {
    const controlSpacing = 16;
    const controlColor = '#f3541e';

    const shouldLoadNow = p.autoPlay || isIOS;

    const playerRef = useRef<HTMLDivElement>(null);
    const [facadeState, setFacadeState] = useState<FacadeState>(
        shouldLoadNow ? FacadeState.Loading : FacadeState.Initial
    );

    const posterStyle = {
        width: '100%',
        aspectRatio: '16/9',
        cursor: 'pointer',
        position: 'absolute',
        top: 0,
        left: 0,
    } as CSSProperties;

    const containerStyle = {
        position: 'relative',
    } as CSSProperties;

    const playStyle = {
        // Extracted from Plyr Styles
        padding: `${controlSpacing * 1.5}px`,
        color: 'white',
        borderRadius: '100%',
        zIndex: 3,
        backgroundColor: controlColor,
    } as CSSProperties;

    const playerStyle = {
        ...p.style,
        overflow: 'hidden',
        '--plyr-color-main': controlColor,
        '--plyr-control-spacing': `${controlSpacing}px`,
        opacity: facadeState === FacadeState.Ready ? '1' : '0',
    } as React.CSSProperties;

    const playIcon =
        facadeState === FacadeState.Loading ? (
            <LoadingOutlined style={playStyle} />
        ) : (
            <FontAwesomeIcon style={playStyle} icon={faPlay} />
        );

    React.useEffect(() => {
        const shouldLoad =
            playerRef.current !== null && facadeState !== FacadeState.Initial;
        if (!shouldLoad) return;
        import('plyr').then(({ default: Plyr }) => {
            if (!shouldLoad) return;
            const player = new Plyr(playerRef.current, {
                fullscreen: {
                    iosNative: true,
                    fallback: false,
                },
                autoplay: true,
            });

            if (p.autoPlay) player.play();

            player.once('play', () => setFacadeState(FacadeState.Ready));
        });
    }, [playerRef.current, facadeState]);

    const { width, height } = getImageDimensions(p.poster ?? '');

    return (
        <div style={containerStyle}>
            <div style={playerStyle} className={p.className}>
                <div
                    ref={playerRef}
                    data-plyr-provider="vimeo"
                    data-plyr-embed-id={p.url}
                    data-poster={p.poster}
                />
            </div>
            <div
                onClick={() => setFacadeState(FacadeState.Loading)}
                style={{
                    ...posterStyle,
                }}
                className={
                    facadeState === FacadeState.Ready
                        ? 'hidden'
                        : 'vertical center-middle'
                }>
                <img
                    width={width}
                    height={height}
                    loading="lazy"
                    alt="Poster do vídeo"
                    src={p.poster}
                    style={{
                        width: '100%',
                        height: '100%',
                        zIndex: p.poster ? 1 : -1,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                />
                {playIcon}
            </div>
        </div>
    );
};
